import { HttpParams } from "@angular/common/http";
import { AfterViewInit, Component, ContentChild, ContentChildren, forwardRef, Inject, Input, OnInit, QueryList, ViewContainerRef } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { take } from "rxjs";
import { ErrorMessages } from "../../common/constants";
import { Location, LocationSchema, Metric, MetricType } from "../../model";
import { DateRangeName } from "../../service/date-range.service";
import { MetricService } from "../../service/metric.service";
import { AbstractContextService } from "../../shared/class/abstract-context-service.class";
import { AbstractThingContextService } from "../../shared/class/abstract-thing-context-service.class";
import { MetricDetailComponent } from "../../shared/component";
import { LocationSchemaThingGroupComponent } from "../../shared/component/location-schema-thing-group/location-schema-thing-group.component";
import { ThingSelectionDialogComponent } from "../../shared/component/thing-selection-dialog/thing-selection-dialog.component";
import { ConfirmDialog } from "../../shared/confirm-dialog/confirm-dialog.component";
import { SelectionInputDialogData } from "../../shared/search-field/selection-input/selection-input.component";
import { ThingSelectionInputDialogComponent } from "../../shared/search-field/selection-input/thing-selection-input-dialog/thing-selection-input-dialog.component";
import { ErrorUtility } from "../../utility/error-utility";
import { LayoutType } from "../shared/details-widget";
import { LocationSchemaAddElementDialogBody, LocationSchemaAddElementDialogOperationType, LocationSchemaWidgetAddElementDialogComponent } from "./location-schema-widget-add-element-dialog.component";
import { LocationSchemaService } from "./location-schema-widget.service";

@Component({
    selector: 'location-schema-widget',
    template: require('./location-schema-widget.component.html'),
    styles: [require('./location-schema-widget.component.css')],
    providers: [LocationSchemaService]
})
export class LocationSchemaWidgetComponent implements OnInit, AfterViewInit {

    @Input() title: string;

    @Input() description: string;

    @ContentChildren(LocationSchemaThingGroupComponent) thingGroups: QueryList<LocationSchemaThingGroupComponent>;

    @ContentChild(ThingSelectionDialogComponent) thingSelectionDialog: ThingSelectionDialogComponent;

    @Input() defaultPeriod: DateRangeName;

    @Input() periodRef: string;

    @Input() detailsLayout: LayoutType = LayoutType.TABLE;

    readPermission: boolean;
    locationSchema: LocationSchema;
    error: string;
    refreshing: boolean;
    MoveDirection = MoveDirectionType;
    updating: boolean;
    thingGroupsArray: LocationSchemaThingGroupComponent[];
    workCellExpansionPanelOpenedMap: { [workCellId: string]: boolean } = {};
    selectedView: LocationSchemaView;
    locationSchemaInfo: LocationSchemaInfo;
    metrics: Metric[];

    private location: Location;

    constructor(
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
        @Inject(forwardRef(() => LocationSchemaService)) private locationSchemaService: LocationSchemaService,
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog,
        @Inject(forwardRef(() => ViewContainerRef)) private vcRef: ViewContainerRef,
        @Inject(forwardRef(() => MetricService)) private metricService: MetricService
    ) { }

    ngOnInit(): void {
        this.location = this.contextService.getCurrentLocation();
    }

    ngAfterViewInit(): void {
        if (!this.thingGroups?.toArray()?.length || this.thingGroups.toArray().some(group => !group.columnComponents?.toArray()?.length)) {
            console.error("Invalid configuration: missing or invalid thingGroup element");
        } else {
            this.readPermission = this.location && !this.thingContextService.getCurrentThing();
            if (this.readPermission) {
                this.validateThingGroups().then(() => {
                    this.initLocationSchema();
                }).catch(err => this.handleError(err));
            }
        }
    }

    private initLocationSchema(): void {
        this.refreshing = true;
        this.locationSchemaService.getLocationSchemaByLocationId(this.location.id).then(locationSchema => {
            this.locationSchema = locationSchema || { id: null, locationId: this.location.id, productionLines: null };
            this.error = null;
            this.refreshing = false;
            this.updating = false;
            this.locationSchemaInfo = this.getLocationSchemaInfo();
            this.selectedView = this.isViewToggleVisible() ? LocationSchemaView.PLAN : LocationSchemaView.TABLE;
        }).catch(err => this.handleError(err));
    }

    private validateThingGroups(): Promise<void> {
        let thingGroupsArray = [];
        const params = new HttpParams().set('locationId', this.location.id);
        return this.metricService.getAllMetricsAssociatedToThings(params).then(metrics => {
            this.metrics = metrics;
            this.thingGroups.toArray().forEach(group => {
                let componentsArray = [];
                group.columnComponents.toArray().forEach(component => {
                    if (component instanceof MetricDetailComponent) {
                        const metric = this.metrics.find(m => m.name == component.name);
                        if (!metric) {
                            console.error("Invalid metric " + component.name);
                        } else {
                            if (metric.type != MetricType.ALGORITHM) {
                                component.aggregation = null;
                            }
                            component.filter = component.filter != undefined ? component.filter : "dictionary:{'showIcon':false,'showValue':true,'showLabel':false}";
                            componentsArray.push(component);
                        }
                    } else {
                        componentsArray.push(component);
                    }
                });
                group.columnComponents.reset(componentsArray);
                thingGroupsArray.push(group);
            });
            this.thingGroupsArray = thingGroupsArray;
        });
    }

    private handleError(err): void {
        this.refreshing = false;
        this.updating = false;
        this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
    }

    addOrEditProductionLine(productionLineIndex: number): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        const data: LocationSchemaAddElementDialogBody = {
            locationSchema: this.locationSchema,
            operationType: productionLineIndex != null ? LocationSchemaAddElementDialogOperationType.EDIT_PRODUCTION_LINE : LocationSchemaAddElementDialogOperationType.ADD_PRODUCTION_LINE,
            productionLineIndex: productionLineIndex,
            workCellIndex: null
        }
        dialogConfig.data = data;
        this.dialog.open(LocationSchemaWidgetAddElementDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                this.locationSchema = result;
                this.locationSchemaInfo = this.getLocationSchemaInfo();
            }
        });
    }

    addOrEditWorkCell(productionLineIndex: number, workCellIndex: number): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            locationSchema: this.locationSchema,
            operationType: workCellIndex != null ? LocationSchemaAddElementDialogOperationType.EDIT_WORK_CELL : LocationSchemaAddElementDialogOperationType.ADD_WORK_CELL,
            productionLineIndex: productionLineIndex,
            workCellIndex: workCellIndex
        }
        this.dialog.open(LocationSchemaWidgetAddElementDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                this.locationSchema = result;
                if (workCellIndex == null) {      // expand newly created work cell
                    const workCellId = this.locationSchema.productionLines[productionLineIndex].workCells.slice(-1)[0].id;
                    if (workCellId) {
                        this.workCellExpansionPanelOpenedMap[workCellId] = true;
                    }
                }
                this.locationSchemaInfo = this.getLocationSchemaInfo();
            }
        });
    }

    deleteProductionLine(productionLineIndex: number): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.panelClass = "delete-production-line-dialog";
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            title: "deleteProductionLineMessage",
            message: "deleteProductionLineInfoMessage"
        }
        this.dialog.open(ConfirmDialog, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                this.locationSchema.productionLines.splice(productionLineIndex, 1);
                this.updateLocationSchema();
            }
        });
    }

    deleteWorkCell(productionLineIndex: number, workCellIndex: number): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.panelClass = "delete-work-cell-dialog";
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            title: "deleteWorkCellMessage",
            message: "deleteWorkCellInfoMessage"
        }
        this.dialog.open(ConfirmDialog, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                this.locationSchema.productionLines[productionLineIndex].workCells.splice(workCellIndex, 1);
                this.updateLocationSchema();
            }
        });
    }

    moveProductionLine(productionLineIndex: number, direction: MoveDirectionType): void {
        const productionLineToMove = this.locationSchema.productionLines[productionLineIndex];
        const newIndex = this.getNewIndex(productionLineIndex, this.locationSchema.productionLines.length, direction);
        this.locationSchema.productionLines.splice(productionLineIndex, 1);
        this.locationSchema.productionLines.splice(newIndex, 0, productionLineToMove);
        this.updateLocationSchema();
    }

    moveWorkCell(productionLineIndex: number, workCellIndex: number, direction: MoveDirectionType): void {
        const workCellToMove = this.locationSchema.productionLines[productionLineIndex].workCells[workCellIndex];
        const newIndex = this.getNewIndex(workCellIndex, this.locationSchema.productionLines[productionLineIndex].workCells.length, direction);
        this.locationSchema.productionLines[productionLineIndex].workCells.splice(workCellIndex, 1);
        this.locationSchema.productionLines[productionLineIndex].workCells.splice(newIndex, 0, workCellToMove);
        this.updateLocationSchema();
    }

    private getNewIndex(currentIndex: number, totalElementCount: number, direction: MoveDirectionType): number {
        switch (direction) {
            case MoveDirectionType.TOP:
                return 0;
            case MoveDirectionType.BOTTOM:
                return totalElementCount - 1;
            case MoveDirectionType.UP:
                return currentIndex - 1;
            case MoveDirectionType.DOWN:
                return currentIndex + 1;
        }
    }

    addWorkCellDevice(productionLineIndex: number, workCellIndex: number): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.maxWidth = '800px';
        dialogConfig.autoFocus = false;
        const data: SelectionInputDialogData = {
            columnComponents: this.thingSelectionDialog?.columnComponents || new QueryList(),
            queryId: null,
            advancedSearchBody: null,
            title: 'addDeviceProperty'
        };
        dialogConfig.data = data;
        dialogConfig.viewContainerRef = this.vcRef;
        this.dialog.open(ThingSelectionInputDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(selectedIds => {
            if (selectedIds) {
                if (!this.locationSchema.productionLines[productionLineIndex].workCells[workCellIndex].thingIds) {
                    this.locationSchema.productionLines[productionLineIndex].workCells[workCellIndex].thingIds = selectedIds;
                } else {
                    selectedIds.forEach(id => {
                        if (!this.locationSchema.productionLines[productionLineIndex].workCells[workCellIndex].thingIds.includes(id)) {
                            this.locationSchema.productionLines[productionLineIndex].workCells[workCellIndex].thingIds.push(id);
                        }
                    });
                }
                this.updateLocationSchema();
            }
        });
    }

    deleteDevice(productionLineIndex: number, workCellIndex: number, thingId: string): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.panelClass = "delete-device-dialog";
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            title: "deleteDeviceMessage",
            message: "deleteDeviceInfoMessage"
        }
        this.dialog.open(ConfirmDialog, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                const deviceIndex = this.locationSchema.productionLines[productionLineIndex].workCells[workCellIndex].thingIds.findIndex(id => id == thingId);
                if (deviceIndex > -1) {
                    this.locationSchema.productionLines[productionLineIndex].workCells[workCellIndex].thingIds.splice(deviceIndex, 1);
                    this.updateLocationSchema();
                } else {
                    this.error = 'Invalid device identifier';
                }
            }
        });
    }

    moveDeviceToWorkCell(productionLineIndex: number, workCellIndex: number, body: { thingId: string, workCellId: string }): void {
        if (!body.workCellId) {
            this.error = "Invalid Work Cell";
            return;
        }
        const deviceIndex = this.locationSchema.productionLines[productionLineIndex].workCells[workCellIndex].thingIds.findIndex(id => id == body.thingId);
        let destinationProductionLineIndex: number;
        let destinationWorkCellIndex: number;
        this.locationSchema.productionLines.forEach((pl, plIndex) => {
            pl.workCells?.forEach((wc, wcIndex) => {
                if (body.workCellId == wc.id) {
                    destinationProductionLineIndex = plIndex;
                    destinationWorkCellIndex = wcIndex;
                }
            });
        });
        if (deviceIndex > -1 && destinationProductionLineIndex != null && destinationWorkCellIndex != null) {
            this.locationSchema.productionLines[productionLineIndex].workCells[workCellIndex].thingIds.splice(deviceIndex, 1);
            if (!this.locationSchema.productionLines[destinationProductionLineIndex].workCells[destinationWorkCellIndex].thingIds) {
                this.locationSchema.productionLines[destinationProductionLineIndex].workCells[destinationWorkCellIndex].thingIds = [body.thingId];
            } else {
                if (!this.locationSchema.productionLines[destinationProductionLineIndex].workCells[destinationWorkCellIndex].thingIds.includes(body.thingId)) {
                    this.locationSchema.productionLines[destinationProductionLineIndex].workCells[destinationWorkCellIndex].thingIds.push(body.thingId);
                }
            }
            this.updateLocationSchema();
        } else {
            this.error = "Invalid device or Work Cell";
        }
    }

    private updateLocationSchema(): void {
        this.updating = true;
        this.locationSchemaService.saveLocationSchema(this.locationSchema).then(updatedLocationSchema => {
            this.locationSchema = updatedLocationSchema;
            this.updating = false;
            this.locationSchemaInfo = this.getLocationSchemaInfo();
        }).catch(err => this.handleError(err));
    }

    toggleExpansionPanel(event: any, workCellId: string): void {
        event.stopPropagation();
        const isCurrentlyOpen = this.workCellExpansionPanelOpenedMap[workCellId] || false;
        this.workCellExpansionPanelOpenedMap[workCellId] = !isCurrentlyOpen;
    }

    isViewToggleVisible(): boolean {
        return this.locationSchema?.productionLines?.some(pl => pl.workCells?.some(wc => wc.thingIds?.length > 0));
    }

    private getLocationSchemaInfo(): LocationSchemaInfo {
        let plCount = 0;
        let wcCount = 0;
        let deviceCount = 0;
        this.locationSchema?.productionLines?.forEach(pl => {
            plCount++;
            pl.workCells?.forEach(wc => {
                wcCount++;
                deviceCount += (wc.thingIds?.length || 0);
            });
        });
        return { productionLineCount: plCount, workCellCount: wcCount, deviceCount: deviceCount };
    }
}

enum MoveDirectionType {
    TOP = "TOP",
    BOTTOM = "BOTTOM",
    UP = "UP",
    DOWN = "DOWN"
}

enum LocationSchemaView {
    TABLE = 'TABLE',
    PLAN = 'PLAN'
}

class LocationSchemaInfo {
    productionLineCount: number;
    workCellCount: number;
    deviceCount: number;
}

